import { Box, Typography, List, ListItem, FormControlLabel, Switch } from "@mui/material";
import { Link } from "react-router-dom";
import theme from "../styles/theme";

interface Player {
  id: number;
  username: string;
  nom_complet?: string;
}

interface ExerciseRankingProps {
  exercise: {
    id: number;
    title: string;
    description: string;
  };
  categoryName?: string;
  ranking: { player: Player; rate: number }[];
  assignments: {
    mobilite: boolean;
    puissance: boolean;
    construction: boolean;
    agressivite: boolean;
  };
  onToggleCategory: (exerciseId: number, category: 'mobilite' | 'puissance' | 'construction' | 'agressivite') => void;
}

export default function ExerciseRanking({
  exercise,
  categoryName,
  ranking,
  assignments,
  onToggleCategory,
}: ExerciseRankingProps) {
  const firstRate = ranking.length > 0 ? ranking[0].rate : 0;

  return (
    <Box
      sx={{
        borderRadius: theme.shape.borderRadius,
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
        p: 2,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontSize: "1.1rem",
          fontWeight: "bold",
          color: theme.palette.primary.main,
          mb: 1,
        }}
      >
        {categoryName ? `${categoryName} - ` : ""}
        {exercise.title}
      </Typography>

      <List dense>
        {ranking.map((entry, idx) => {
          let color = theme.palette.success.main;
          let colorDark = theme.palette.success.dark;
          if (entry.rate < 30) {
            color = theme.palette.error.dark;
            colorDark = "#c62828";
          } else if (entry.rate < 45) {
            color = theme.palette.error.main;
            colorDark = "#d32f2f";
          } else if (entry.rate < 60) {
            color = theme.palette.warning.main;
            colorDark = "#f57c00";
          } else if (entry.rate < 70) {
            color = theme.palette.warning.light;
            colorDark = "#ffa726";
          } else if (entry.rate < 85) {
            color = theme.palette.info.main;
            colorDark = "#1976d2";
          } else {
            color = theme.palette.success.main;
            colorDark = "#388e3c";
          }

          const isGrayed = entry.rate === 0;
          const diff = idx === 0 ? 0 : Math.round(firstRate - entry.rate);

          return (
            <ListItem
              key={entry.player.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                opacity: isGrayed ? 0.75 : 1,
                filter: isGrayed ? "grayscale(0.9)" : "none",
              }}
            >
              <Box
                component={Link}
                to={`/player/${entry.player.id}`}
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontWeight: "bold",
                }}
              >
                {idx + 1}. {entry.player.nom_complet || entry.player.username}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {idx !== 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.9rem",
                      ml: 1,
                    }}
                  >
                    -{diff}
                  </Typography>
                )}
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: theme.shape.borderRadius,
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    color: "white",
                    background: `linear-gradient(135deg, ${color} 0%, ${colorDark} 100%)`,
                    minWidth: "58px",
                    textAlign: "center",
                    boxShadow: `0 2px 8px ${color}40`,
                    border: `1px solid ${colorDark}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    letterSpacing: "0.5px",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: `0 4px 12px ${color}60`,
                    },
                  }}
                >
                  {entry.rate}%
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>

      <Typography
        variant="body2"
        sx={{ mt: 1, color: theme.palette.text.secondary }}
      >
        {exercise.description}
      </Typography>

      <br />
      <Typography
        variant="body2"
        sx={{ mt: 1, color: theme.palette.text.secondary, mb: 1 , textAlign: 'center', fontWeight: 'bold'}}
      >
        Assigner une dimension supp. à cet exercice
      </Typography>
      {/* Switches pour catégories spéciales */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, justifyContent: 'space-evenly' }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={assignments.mobilite}
              onChange={() => onToggleCategory(exercise.id, "mobilite")}
              color="secondary"
            />
          }
          label={<Typography variant="caption">🏃 Mobilité</Typography>}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={assignments.puissance}
              onChange={() => onToggleCategory(exercise.id, "puissance")}
              color="secondary"
            />
          }
          label={<Typography variant="caption">💪 Puissance</Typography>}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={assignments.construction}
              onChange={() => onToggleCategory(exercise.id, "construction")}
              color="secondary"
            />
          }
          label={<Typography variant="caption">🏗️ Construction</Typography>}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={assignments.agressivite}
              onChange={() => onToggleCategory(exercise.id, "agressivite")}
              color="secondary"
            />
          }
          label={<Typography variant="caption">⚔️ Agressivité</Typography>}
        />
      </Box>
    </Box>
  );
}
